import React, { useEffect, useState } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';

import {
  UpdateRegionHeroesPayload,
  getExperienceOfferById,
  getHeroesByRegion,
  updateRegionHeroes,
} from '~/services/ExperiencesService';

import { HeroesListItem } from './HeroesListItem';

type ContentProps = {
  selectedExperiences: Array<Experiences.Offer>;
  onRemoveExperience: (experience: Experiences.Offer) => void;
  onLoadSelectedExperiences: (experiences: Array<Experiences.Offer>) => void;
  region: UpdateRegionHeroesPayload['region'];
  placeId: string;
  brand: UpdateRegionHeroesPayload['brand'];
};

export type HeroesListRef = {
  validate: () => boolean;
};

function HeroesList({
  selectedExperiences,
  onRemoveExperience,
  onLoadSelectedExperiences,
  region,
  placeId,
  brand,
}: ContentProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const editHero = async () => {
    setIsLoading(true);
    try {
      const payload = {
        experiencesOffersIds: selectedExperiences.map((item) => item.id),
        brand,
        region,
      };

      await updateRegionHeroes(placeId, payload);
      enqueueSnackbar('Changed heroes with success!', { variant: 'success' });
    } catch (error) {
      console.error('ERROR ON UPDATE HERO: ', error);
      enqueueSnackbar(`${error.status}: ${error.message}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHeroes = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const res = await getHeroesByRegion(region, brand, placeId);
      if (res.errors?.length > 0) {
        enqueueSnackbar("[ERROR] Don't exists heroes response.", { variant: 'error' });
        return;
      }

      const { experiencesOffersIds } = res.result;
      const experienceOffersRequests = await Promise.all(
        experiencesOffersIds.map((id) => getExperienceOfferById({ id, brand, curationData: true })),
      );

      if (experienceOffersRequests.some((res) => res.errors?.length)) {
        enqueueSnackbar("[ERROR] Don't exists heroes response.", { variant: 'error' });
        return;
      }

      const experiences = experienceOffersRequests.flatMap((res) => res.result);
      onLoadSelectedExperiences(experiences);
      setIsLoading(false);
    } catch (error) {
      console.error('ERROR ON FETCH HEROES: ', error);
      if (error.status === 404) {
        return;
      }
      enqueueSnackbar(`${error.status}: ${error.message}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onLoadSelectedExperiences([]);
    fetchHeroes();
  }, [region, placeId, brand]);

  const onSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIndex = selectedExperiences.findIndex((item) => item.id === active.id);
      const newIndex = selectedExperiences.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newArray = arrayMove(selectedExperiences, oldIndex, newIndex);
        onLoadSelectedExperiences(newArray);
      }
    }
  };

  const shouldShowContent = region && placeId;

  return (
    <Box>
      <PageSubheader title="Curated list"></PageSubheader>

      <Box>
        {isLoading && (
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Spinner size={32} />
          </Stack>
        )}

        {!isLoading && shouldShowContent && (
          <Stack direction="column" spacing={2}>
            {selectedExperiences.length > 3 && (
              <Alert severity="warning" style={{ textAlign: 'center' }}>
                In the <strong>hotel flow</strong>, only the 3 best offers available in this list will be shown.
              </Alert>
            )}
            {selectedExperiences.length ? (
              <DndContext onDragEnd={onSortEnd}>
                <SortableContext items={selectedExperiences}>
                  <Stack direction="column" spacing={1}>
                    {selectedExperiences.map((value) => {
                      return (
                        <HeroesListItem key={`item-${value.id}`} removeOnclick={onRemoveExperience} value={value} />
                      );
                    })}
                  </Stack>
                </SortableContext>
              </DndContext>
            ) : (
              <Typography>No offers have been added yet for this city</Typography>
            )}

            <Box>
              <Button variant="contained" onClick={editHero} disabled={!region || !placeId}>
                Save
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default HeroesList;
